import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Lock, Eye, EyeOff, User, ChevronLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { LogoStacked } from '../components/Logo';
import { useApp } from '../context/AppContext';
import { IMAGES, CONTACT, waHref, SHOW_WHATSAPP } from '../lib/constants';
import { isNative } from '../lib/native';
import { startGoogleLogin } from '../pages/GoogleCallback';
import { Screen, PrimaryButton, MobileInput } from './PhoneFrame';

export const Welcome = () => {
  const navigate = useNavigate();
  const { customer } = useApp();
  return (
    <div className="flex-1 relative flex flex-col">
      <img src={IMAGES.burger} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/85" />
      <div className="relative flex-1 flex flex-col items-center text-center text-white px-6 pt-24">
        <LogoStacked dark markSize={100} />
        <p className="mt-4 text-lg font-semibold">Tu ciudad, en tus manos</p>
        <div className="flex-1" />
        <p className="text-[15px] text-white/90 font-medium mb-6 leading-snug">Comida, Mandaditos 7,<br />Envíos y mucho más.</p>
        <PrimaryButton data-testid="welcome-start" onClick={() => navigate(customer ? '/app/home' : '/app/register')}>Comenzar</PrimaryButton>
        <PrimaryButton data-testid="welcome-login" variant="dark" className="mt-3 mb-8 border border-white/30" onClick={() => navigate(customer ? '/app/home' : '/app/login')}>Iniciar sesión</PrimaryButton>
      </div>
    </div>
  );
};

const Flag = () => (
  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600">ES <span className="w-6 h-4 rounded-sm overflow-hidden inline-flex flex-col"><span className="flex-1 bg-[#0073CF]" /><span className="flex-1 bg-white" /><span className="flex-1 bg-[#0073CF]" /></span></span>
);

const SocialButtons = () => (isNative() ? null : (
  <>
    <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400">o continúa con</span><div className="flex-1 h-px bg-gray-200" /></div>
    <div className="grid grid-cols-1 gap-3">
      <button type="button" data-testid="google-login" onClick={startGoogleLogin} className="h-12 rounded-xl border border-gray-200 flex items-center justify-center gap-2 font-semibold text-sm hover:bg-gray-50">
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 35.4 44 30.1 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg> Google
      </button>
    </div>
  </>
));

export const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useApp();
  const [phone, setPhone] = useState('');
  const [pass, setPass] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!phone || !pass) return toast.error('Ingresa tu teléfono y contraseña');
    setBusy(true);
    try {
      const c = await signIn('customer', phone, pass);
      toast.success(`¡Bienvenido de nuevo, ${(c.name || 'Cliente').split(' ')[0]}!`);
      navigate('/app/home');
    } catch { /* toast shown by context */ } finally { setBusy(false); }
  };
  return (
    <Screen>
      <div className="flex items-center justify-between px-4 h-12"><button onClick={() => navigate('/app')} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center hover:bg-gray-100"><ChevronLeft size={24} /></button><Flag /></div>
      <form onSubmit={submit} className="px-6 pt-2">
        <LogoStacked markSize={64} />
        <h1 className="text-2xl font-extrabold text-center mt-5">Inicia sesión</h1>
        <p className="text-center text-gray-500 text-sm mt-1">Ingresa tus datos para continuar</p>
        <div className="space-y-3 mt-6">
          <MobileInput data-testid="login-phone" icon={Smartphone} label="Número de teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+504  XXXX-XXXX" />
          <MobileInput data-testid="login-password" icon={Lock} label="Contraseña" type={show ? 'text' : 'password'} value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Contraseña" right={<button type="button" onClick={() => setShow((s) => !s)} className="text-gray-400">{show ? <EyeOff size={20} /> : <Eye size={20} />}</button>} />
        </div>
        <PrimaryButton data-testid="login-submit" type="submit" className="mt-5" disabled={busy}>{busy ? 'Ingresando...' : 'Iniciar sesión'}</PrimaryButton>
        <button type="button" onClick={() => navigate('/app/forgot')} className="w-full text-center text-sm text-[#2F80ED] font-medium mt-3">¿Olvidaste tu contraseña?</button>
        <SocialButtons />
        <p className="text-center text-sm text-gray-600 mt-6">¿No tienes cuenta? <button type="button" onClick={() => navigate('/app/register')} className="text-[#EE3B36] font-bold underline">Regístrate</button></p>
      </form>
    </Screen>
  );
};

export const Register = () => {
  const navigate = useNavigate();
  const { signUpCustomer } = useApp();
  const [f, setF] = useState({ name: '', phone: '', email: '', pass: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.pass) return toast.error('Completa los campos obligatorios');
    if (f.pass.length < 6) return toast.error('La contraseña debe tener al menos 6 caracteres');
    setBusy(true);
    try {
      await signUpCustomer({ name: f.name, phone: f.phone, email: f.email || null, password: f.pass });
      toast.success('Cuenta creada. ¡Bienvenido a Delibery 7!');
      navigate('/app/home');
    } catch { /* toast shown by context */ } finally { setBusy(false); }
  };
  return (
    <Screen>
      <div className="flex items-center justify-between px-4 h-12"><button onClick={() => navigate('/app')} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center hover:bg-gray-100"><ChevronLeft size={24} /></button><Flag /></div>
      <form onSubmit={submit} className="px-6 pt-1">
        <LogoStacked markSize={56} />
        <h1 className="text-2xl font-extrabold text-center mt-4">Crear cuenta</h1>
        <p className="text-center text-gray-500 text-sm mt-1">Regístrate para comenzar a pedir</p>
        <div className="space-y-3 mt-5">
          <MobileInput data-testid="reg-name" icon={User} label="Nombre completo" value={f.name} onChange={set('name')} placeholder="Juan Pérez" />
          <MobileInput data-testid="reg-phone" icon={Smartphone} label="Número de teléfono" value={f.phone} onChange={set('phone')} placeholder="+504  XXXX-XXXX" />
          <MobileInput icon={Mail} label="Correo (opcional)" type="email" value={f.email} onChange={set('email')} placeholder="correo@ejemplo.com" />
          <MobileInput data-testid="reg-pass" icon={Lock} label="Contraseña" type="password" value={f.pass} onChange={set('pass')} placeholder="Mínimo 6 caracteres" />
        </div>
        <PrimaryButton data-testid="reg-submit" type="submit" className="mt-5" disabled={busy}>{busy ? 'Creando cuenta...' : 'Registrarme'}</PrimaryButton>
        <SocialButtons />
        <p className="text-[11px] text-gray-400 text-center mt-3 px-4">Al registrarte aceptas los <span className="text-[#1E2130] font-semibold">términos y condiciones</span> de Delibery 7.</p>
        <p className="text-center text-sm text-gray-600 mt-5">¿Ya tienes cuenta? <button type="button" onClick={() => navigate('/app/login')} className="text-[#EE3B36] font-bold underline">Inicia sesión</button></p>
      </form>
    </Screen>
  );
};

export const Forgot = () => {
  const navigate = useNavigate();
  const { requestPasswordReset } = useApp();
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!phone.trim()) return toast.error('Escribe tu número de teléfono');
    setBusy(true);
    try {
      await requestPasswordReset('customer', phone);
      if (SHOW_WHATSAPP) {
        toast.success('Solicitud enviada. Te contactaremos por WhatsApp.');
        window.open(waHref(CONTACT.whatsapp, `Hola, olvidé mi contraseña de Delibery 7. Mi teléfono es ${phone}`), '_blank');
      } else {
        toast.success(`Solicitud enviada. Llámanos al ${CONTACT.callCenter[0]} para recibir tu contraseña nueva.`);
      }
      navigate('/app/login');
    } catch { /* toast shown by context */ } finally { setBusy(false); }
  };
  return (
    <Screen>
      <div className="flex items-center px-4 h-12"><button onClick={() => navigate(-1)} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center hover:bg-gray-100"><ChevronLeft size={24} /></button></div>
      <div className="px-6 pt-6">
        <LogoStacked markSize={56} />
        <h1 className="text-2xl font-extrabold text-center mt-5">Recuperar cuenta</h1>
        <p className="text-center text-gray-500 text-sm mt-1">{SHOW_WHATSAPP ? 'Enviamos tu solicitud al soporte de Delibery 7 y te escribimos por WhatsApp para darte una contraseña nueva.' : `Enviamos tu solicitud al soporte de Delibery 7. Llámanos al ${CONTACT.callCenter[0]} y te damos una contraseña nueva.`}</p>
        <MobileInput className="mt-6" data-testid="forgot-phone" icon={Smartphone} label="Número de teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+504  XXXX-XXXX" />
        <PrimaryButton className="mt-5" data-testid="forgot-submit" disabled={busy} onClick={submit}>{busy ? 'Enviando...' : (SHOW_WHATSAPP ? 'Pedir ayuda por WhatsApp' : 'Pedir ayuda al soporte')}</PrimaryButton>
      </div>
    </Screen>
  );
};
